import { inject, Injectable } from '@angular/core';
import { LoggableAPI } from '../models/protocols/loggable-api';
import { ApiClient } from './api-client';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MediaUtils } from '../utils/media-utils';
import { CustomError } from '../models/shared/custom-error';
import { HttpHeaders } from '@angular/common/http';
import * as buffer from 'buffer';
import { AttachmentFile } from '../models/users/dto/attachment-file';
import { Endpoints } from './endpoints';
import { SubmissionAttachment } from '../models/submissions/submission-attachment';
import { EmployerSubmissionAttachment } from '../models/submissions/employer-submissions/employer-submission-attachment';

@Injectable({
  providedIn: 'root'
})
export class AttachmentApi implements LoggableAPI {
  constructor() {}

  private apiClient = inject(ApiClient);

  public serviceName = 'Attachments';

  public uploadFile(file: File, attachmentId: string, presignedUrl: string): Observable<any> {
    return from(MediaUtils.fileToUserFile(file)).pipe(
      switchMap(userFile => {
        const attachmentFile = new AttachmentFile(file, attachmentId, userFile.fileUrl, presignedUrl);
        return this.uploadAttachmentFile(attachmentFile);
      })
    );
  }

  public uploadSubmissionFiles(
    attachments: SubmissionAttachment[],
    files: { file: File; fileName: string }[]
  ): Observable<unknown> {
    const uploads = files.map(({ file, fileName }) => {
      const attachment = attachments.find(a => a.attachment.fileName === fileName);
      const presignedUrl = attachment?.attachment.links?.[0]?.presignedUrl;
      if (!attachment || !presignedUrl) return of(false);
      return this.uploadFile(file, attachment.id, presignedUrl).pipe(
        map(() => true),
        catchError(() => of(false))
      );
    });
    if (!uploads.length) return of(null);
    return forkJoin(uploads).pipe(
      switchMap(results => {
        if (results.every(uploaded => uploaded)) return of(results);
        return throwError(() => new Error('A file failed to upload'));
      })
    );
  }

  public uploadAttachmentFile(file: AttachmentFile): Observable<any> {
    const fileType = file.getFileType();
    const headers = new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': fileType,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Encoding': 'base64',
      'x-amz-meta-attachmentid': file.attachmentId
    });
    const replacementString = 'data:' + fileType + ';base64,';
    const newFileName = file.fileUrl.replace(replacementString, '');
    const buff = buffer.Buffer.from(newFileName, 'base64');

    const blob = new Blob([new Uint8Array(buff)]);
    return this.apiClient.putAssetToS3(file.presignedUrl, blob, headers).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipDeleteMemberSubmissionAttachment(attachmentId: string, submissionId: string): Observable<any> {
    const url = Endpoints.ipDeleteMemberSubmissionAttachment(attachmentId, submissionId);
    return this.apiClient.deleteWithUntypedRes(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipDeleteEmployerSubmissionAttachment(attachmentId: string, submissionId: string): Observable<any> {
    const url = Endpoints.ipDeleteEmployerSubmissionAttachment(attachmentId, submissionId);
    return this.apiClient.deleteWithUntypedRes(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberSubmissionAttachments(submissionId: string): Observable<SubmissionAttachment[]> {
    const url = Endpoints.ipGetMemberSubmissionAttachments(submissionId);
    return this.apiClient.getArr<SubmissionAttachment>(SubmissionAttachment, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerSubmissionAttachments(submissionId: string): Observable<EmployerSubmissionAttachment[]> {
    const url = Endpoints.ipGetEmployerSubmissionAttachments(submissionId);
    return this.apiClient.getArr<EmployerSubmissionAttachment>(EmployerSubmissionAttachment, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }
}
